import { Hub, Organization } from '@/models/business-unit'
import { injectWithSelf } from '@/utils/injectWithSelf'
import { computed, inject, InjectionKey, provide, ref, Ref, unref } from 'vue'
import { useStore } from 'vuex-composition-helpers'

type HubContextType = {
  hubId: Ref<string | null | undefined>
}
const HubContextKey: InjectionKey<HubContextType> = Symbol('HubContext')

type MaybeRef<T> = Ref<T> | T

type HubContextProviderOptions = {
  override?: boolean
}

export const useHubContextProvider = (
  hubId: MaybeRef<string | null | undefined>,
  { override = false }: HubContextProviderOptions = {},
) => {
  // Preserve the nearest provider unless this component is an explicit context boundary.
  const existing = inject(HubContextKey, null)
  if (existing && !override) {
    return
  }
  const _hubId = computed(() => unref(hubId))
  provide(HubContextKey, { hubId: _hubId })
}

export const useHubs = () => {
  const { hubId: injectedHubId } = injectWithSelf(HubContextKey, () => ({
    hubId: ref(''),
  }))

  const store = useStore()
  const getBusinessUnitById = store.getters['businessUnits/getBusinessUnitById']

  const accessibleHubs = computed<Hub[]>(() => {
    const hubIds = store.getters['permissions/getAccessibleHubIds']
    return hubIds.map((id) => getBusinessUnitById(id))
  })

  const isMultiHub = computed(() => accessibleHubs.value.length > 1)

  const currentHub = computed<Hub | null>(() => {
    if (injectedHubId.value) {
      return getBusinessUnitById(injectedHubId.value) || null
    }
    return null
  })

  const currentHubOwner = computed<Organization | null>(() => {
    if (currentHub.value && currentHub.value.ownerId) {
      return getBusinessUnitById(currentHub.value.ownerId) || null
    }
    return null
  })

  return { accessibleHubs, isMultiHub, currentHub, currentHubOwner }
}
